/**
 * FormDataProcessor - Utility class for processing form data
 *
 * This utility helps transform form data to use idx property as keys
 * with comprehensive field information and all available options for auditing and reporting.
 */
export default class FormDataProcessor {
  /**
   * Transforms raw form values (field_x_y_z keys) to structured data (using idx as keys)
   * @param {Object} formValues - The form values with field_x_y_z keys
   * @param {Object} formStructure - The form structure with fields that have idx properties
   * @returns {Object} - Structured data with idx as keys
   */
  static transformToStructuredData(formValues, formStructure) {
    if (!formValues) {
      return {};
    }

    const result = {};

    // Process each field using the fieldInfoMap to map field_x_y_z to idx
    const fields = formStructure.fields || [];

    if (!fields || !Array.isArray(fields)) {
      console.warn("No fields found in form structure");
      return {};
    }

    fields.forEach(field => {
      if (!field || !field.fieldId || !field.idx) {
        return;
      }

      const rawValue = formValues[field.fieldId];

      // Skip undefined values
      if (rawValue === undefined) {
        return;
      }

      // Structure the field data
      result[field.idx] = {
        value: rawValue,
        type: field.type,
        label: field.label,
        required: field.required
      };

      // Add additional field metadata
      if (field.validation) {
        result[field.idx].validation = field.validation;
      }

      if (field.entryLimits) {
        result[field.idx].entryLimits = field.entryLimits;
      }

      if (field.componentFields && field.type === "table") {
        result[field.idx].componentFields = field.componentFields;
      }

      if (field.presets) {
        result[field.idx].presets = field.presets;
      }

      // Handle special cases for different field types
      if (
        field.type === "radio" &&
        field.presets &&
        field.presets.staticOptions
      ) {
        result[field.idx].allOptions = field.presets.staticOptions.map(opt => ({
          ...opt,
          isSelected: opt.value === rawValue
        }));
      }
    });

    return result;
  }

  /**
   * Transforms structured data (with idx as keys) to raw form values (field_x_y_z keys)
   * @param {Object} structuredData - The structured data with idx as keys
   * @param {Object} formStructure - The form structure with fields that have fieldId and idx properties
   * @returns {Object} - Raw form values with field_x_y_z keys
   */
  static transformToRawData(structuredData, formStructure) {
    if (!structuredData) {
      return {};
    }

    const result = {};
    const fields = formStructure.fields || [];

    if (!fields || !Array.isArray(fields)) {
      console.warn("No fields found in form structure");
      return {};
    }

    fields.forEach(field => {
      if (!field || !field.fieldId || !field.idx) {
        return;
      }

      const structuredField = structuredData[field.idx];
      if (!structuredField || structuredField.value === undefined) {
        return;
      }

      result[field.fieldId] = structuredField.value;
    });

    return result;
  }

  /**
   * Gets a complete options array including both selected and non-selected options
   * @param {Object} fieldInfo - The field information
   * @param {any} fieldValue - The current field value
   * @returns {Array|null} - Complete options array or null if not applicable
   * @private
   */
  static _getFullOptionsArray(fieldInfo, fieldValue) {
    // Only process fields that can have options
    if (
      !["dropdown", "radio", "checkboxes", "autocomplete"].includes(
        fieldInfo.type
      )
    ) {
      return null;
    }

    // Handle case where presets information is missing
    if (!fieldInfo.presets) {
      return null;
    }

    // For static presets, return the complete options array with selection state
    if (
      fieldInfo.presets.presetsType === "static" &&
      Array.isArray(fieldInfo.presets.staticOptions)
    ) {
      return fieldInfo.presets.staticOptions.map(option => {
        const optionValue =
          typeof option === "object"
            ? option.value || option.identifier
            : option;

        // Determine if this option is selected
        let isSelected = false;
        if (Array.isArray(fieldValue)) {
          // For multi-select fields (checkboxes)
          isSelected = fieldValue.includes(optionValue);
        } else {
          // For single-select fields (radio, dropdown)
          isSelected = fieldValue === optionValue;
        }

        // Return enhanced option object with selection state
        return {
          ...option,
          isSelected
        };
      });
    }

    // For dynamic presets, if we have the full options array in the field info
    if (
      fieldInfo.presets.presetsType === "dynamic" &&
      Array.isArray(fieldInfo.dynamicOptions)
    ) {
      return fieldInfo.dynamicOptions.map(option => {
        const optionId = option.identifier || option.id;

        // Determine if this option is selected
        let isSelected = false;
        if (Array.isArray(fieldValue)) {
          // For array values, check if the value is in the array
          if (typeof fieldValue[0] === "object") {
            // Array of objects - compare by identifier
            isSelected = fieldValue.some(
              item => (item.identifier || item.id) === optionId
            );
          } else {
            // Array of primitives
            isSelected = fieldValue.includes(optionId);
          }
        } else if (typeof fieldValue === "object") {
          // For object values, compare identifiers
          isSelected = (fieldValue.identifier || fieldValue.id) === optionId;
        } else {
          // For primitive values
          isSelected = fieldValue === optionId;
        }

        // Return enhanced option object with selection state
        return {
          ...option,
          isSelected
        };
      });
    }

    // If we can't determine options, return null
    return null;
  }

  /**
   * Builds field mappings from original field IDs to extended field metadata
   * @param {Object} formStructure - The form structure with fields
   * @returns {Object} - Mapping from original keys to field metadata
   * @private
   */
  static _buildFieldMappings(formStructure) {
    const mappings = {};

    if (!formStructure || !formStructure.fields) {
      return mappings;
    }

    // Process each field to create a mapping with extended properties
    formStructure.fields.forEach(field => {
      if (field.fieldId) {
        // Ensure field has an idx
        const fieldIdx = field.idx || field.fieldId;

        // Process component fields for tables to ensure each has a unique idx
        let componentFields = null;
        if (field.type === "table" && Array.isArray(field.componentFields)) {
          componentFields = field.componentFields.map(compField => {
            return {
              ...compField,
              idx: compField.idx || `table-field-${this._generateRandomId()}`
            };
          });
        } else {
          componentFields = field.componentFields;
        }

        mappings[field.fieldId] = {
          // Basic field identification
          idx: fieldIdx,
          fieldId: field.fieldId,

          // Field display properties
          label: field.label || "",
          type: field.type || "text",
          placeholder: field.placeholder || "",

          // Field constraints
          required: field.required || false,
          clearable: field.clearable || false,

          // Field validation
          validation: field.validation || null,
          entryLimits: field.entryLimits || null,

          // Field positioning information for reporting
          position: field.position || {},

          // Field options and presets
          presets: field.presets || null,
          dynamicOptions: field.dynamicOptions || null, // Store cached dynamic options

          // Additional properties for specific field types
          min: field.min,
          max: field.max,
          multiple: field.multiple,
          prependIcon: field.prependIcon,
          thumbLabel: field.thumbLabel,

          // Component fields for table-type fields with guaranteed unique idx
          componentFields: componentFields,

          // Styles for heading-type fields
          styles: field.styles || null
        };
      }
    });

    return mappings;
  }

  /**
   * Generate a random ID for fields that need one
   * @returns {String} - Random ID
   * @private
   */
  static _generateRandomId() {
    return Math.random()
      .toString(36)
      .substring(2, 10);
  }
}
